import React from "react";
import { QuickInput } from "@picklebet/ui";
import { useIsLoggedIn } from "@/features/auth/hooks/useIsLoggedIn";
import { triggerHaptic } from "@/utilities/haptics";
import { convertAmountToCents, convertCentsToAmount } from "common/currency";
import { useCashAvailable } from "hooks/firestore/user/useAccountBalances";
import { useFiatCurrency } from "hooks/firestore/user/useFiatCurrency";

import * as styles from "./QuickStakeChips.module.scss";

export const stakeIncrements = [5, 10, 50, 100];

type QuickStakeChipsProps = {
  isOpen: boolean;
  valueCents: number;
  onSetCents: (cents: number) => void;
  /** Bal fills this instead, when the chips drive a per-combo field. */
  onSetBalanceCents?: (cents: number) => void;
  isPromo: boolean;
  // Ceiling of promo this bet can stake, in total cents (its own stake
  // included). Only read when isPromo.
  maxPromoStakeCents?: number;
  arrowOffset?: string;
  detached?: boolean;
  showBalance?: boolean;
};

export const QuickStakeChips = ({
  isOpen,
  valueCents,
  onSetCents,
  onSetBalanceCents = onSetCents,
  isPromo,
  maxPromoStakeCents = 0,
  arrowOffset,
  detached,
  showBalance = true,
}: QuickStakeChipsProps) => {
  const isLoggedIn = useIsLoggedIn();
  const { available: availableBalanceCents } = useCashAvailable();
  const fiatCurrency = useFiatCurrency();

  const balanceCents = isPromo ? maxPromoStakeCents : availableBalanceCents;
  const balanceValueDollars = convertCentsToAmount(balanceCents);

  const handleAddDollars = (amountDollars: number) => {
    onSetCents(valueCents + convertAmountToCents(amountDollars));
  };

  return (
    <QuickInput.Root
      className={styles.chips}
      onOpened={(element) => element.scrollIntoView({ block: "nearest" })}
      isOpen={isOpen}
      currency={fiatCurrency}
      arrowOffset={arrowOffset}
      detached={detached}
      onClear={() => {
        triggerHaptic.click();
        onSetCents(0);
      }}
    >
      {stakeIncrements.map((amount) => (
        <QuickInput.Option
          key={amount}
          value={amount}
          onClick={() => {
            triggerHaptic.click();
            handleAddDollars(amount);
          }}
        />
      ))}
      {showBalance && isLoggedIn && availableBalanceCents > 0 && (
        <QuickInput.Option
          value={balanceValueDollars}
          label="Bal"
          onClick={() => {
            triggerHaptic.click();
            onSetBalanceCents(balanceCents);
          }}
        />
      )}
    </QuickInput.Root>
  );
};
